import React, {useEffect, useState} from 'react'
import {useForm} from 'react-hook-form'
import {toast} from 'react-toastify'
import {Button} from 'helpers/ui-components/Button'
import {useTranslation} from 'react-i18next'
import {Select2DropdownField} from 'helpers'
import {IFormData, IFormField} from 'models/Common'
import {
 fetchLOB,
 fetchLOBDetails,
 updateLOB,
} from 'utils/api/auth/userManagementService'

type ILOB = {
 lob?: string
}

type ILOBItem = {
 name: string
 value: string
}

type IProps = {
 accessDetails: IFormField | undefined
 userDetails?: IFormField | null
 userRole: number
 loggedInUserId: number | undefined
 setLoader: (newState: boolean) => void
}
const BusinessVertical = (props: IProps) => {
 const {t} = useTranslation()
 const [isDisabled, setIsDisabled] = useState(true)
 const userId = props.userDetails?.id
 const isFpoCeo =
  props.userDetails?.user_category_key === 'FPO CEO'
 const [lobData, setLobData] = useState<ILOBItem[]>([])
 const lobList = [
  {
   id: 'input',
   name: t(
    'userManagement.activate.businessVertical.input'
   ),
  },
  {
   id: 'output',
   name: t(
    'userManagement.activate.businessVertical.output'
   ),
  },
  {
   id: 'finance',
   name: t(
    'userManagement.activate.businessVertical.finance'
   ),
  },
 ]
 const {
  control,
  handleSubmit,
  setValue,
  watch,
  formState: {errors},
 } = useForm({
  mode: 'all',
  reValidateMode: 'onChange',
  defaultValues: {lob: ''},
 })

 const getLOB = async () => {
  props.setLoader(true)
  const res: ILOB = await fetchLOB(Number(userId))
  if (res?.lob) {
   setValue('lob', res.lob)
  }
  props.setLoader(false)
 }

 async function submitFormData(formData: IFormData) {
  if (formData?.lob) {
   props.setLoader(true)
   const res = await updateLOB(
    Number(userId),
    formData.lob.toString()
   )
   if (res) {
    toast.success(res)
   }
   props.setLoader(false)
  }
 }

 const getLOBDetails = async () => {
  props.setLoader(true)

  try {
   const res = await fetchLOBDetails(Number(userId))

   if (Array.isArray(res)) {
    setLobData(res)
   }
  } catch (error) {
   console.error('Error fetching LOB:', error)
  } finally {
   props.setLoader(false)
  }
 }

 const handleEditBtnClick = () => {
  if (props.accessDetails?.activationFlow?.canEdit) {
   setIsDisabled(false)
  }
 }

 useEffect(() => {
  if (isFpoCeo) {
   void getLOBDetails()
  } else {
   void getLOB()
  }
 }, [isFpoCeo])

 return (
  <>
   {isFpoCeo ? (
    <div className='w-full'>
     <div className='text-[#171B1F] flex justify-between text-lg font-normal'>
      <div>
       {t('userManagement.activate.businessVertical.title')}
      </div>
     </div>

     <div className='w-full mt-6'>
      <div className='grid md:grid-cols-3 gap-5 w-full mt-4'>
       {lobData
        .filter(
         (item) => item.value && item.value.trim() !== ''
        )
        .map((item) => (
         <div
          key={item.name}
          className='border border-[#DCE3EA] rounded-xl px-4 py-3 bg-[#F5F8FA] min-h-[78px] flex flex-col justify-center'
         >
          <p className='text-[#4E83E4] text-xs font-medium mb-1'>
           {item.name}
          </p>

          <p className='text-[#33393D] text-base font-medium break-words'>
           {item.value}
          </p>
         </div>
        ))}
      </div>
     </div>
    </div>
   ) : (
    <div className='w-full'>
     <form
      method='POST'
      action=''
      onSubmit={handleSubmit(submitFormData)}
     >
      <div className='text-[#171B1F] flex justify-between text-lg font-normal'>
       <div>
        {t(
         'userManagement.activate.businessVertical.title'
        )}
       </div>

       {props.accessDetails?.activationFlow?.canEdit &&
        (isDisabled ? (
         <Button
          key={'edit'}
          type='button'
          disabled={false}
          onClick={handleEditBtnClick}
          color='blue'
          className='text-center py-[9px] px-6 hover:bg-[#4E83E4] w-[85px]
         !rounded-lg text-base text-[#4E83E4] hover:text-white font-semibold border-[#4E83E4]'
         >
          {t('common.edit')}
         </Button>
        ) : watch('lob') ? (
         <Button
          key={'save'}
          type='submit'
          disabled={false}
          color='blue'
          className='text-center py-[9px] px-6 bg-[#4E83E4]
         !rounded-lg text-base text-white font-semibold border-[#4E83E4]'
         >
          {t('common.save')}
         </Button>
        ) : null)}
      </div>

      <div className='w-full mt-6'>
       <div className='grid md:grid-cols-3 gap-5 w-full mt-4 text-[#171B1F]'>
        <Select2DropdownField
         label={t(
          'userManagement.activate.businessVertical.lob'
         )}
         labelClassName='padding-0 text-[#4E83E4] text-xs !font-medium'
         className={`${
          isDisabled
           ? '!text-[#33393D] font-medium '
           : 'font-normal !text-[#33393D]'
         } boxed-select2 relative block text-lg border-0 !rounded-l-lg`}
         wrapperClassName='relative w-full'
         inputWrapperClassName='pl-4 py-2 px-4 '
         required={true}
         disabled={isDisabled}
         isClearable={false}
         isSearchable={true}
         control={control}
         customStyle={{
          transition: 'unset',
          boxShadow: 'unset',
          border: '0px',
          borderRadius: '0px',
          padding: '0px 0px 0px 0px',
          minHeight: 'auto',
         }}
         data={lobList}
         rules={{
          validate: {
           required: (v: string) => {
            if (v === '')
             return `${t('helperText.plsSelect')} ${t(
              'userManagement.activate.geoMapping.state'
             )}`
           },
          },
         }}
         id='lob'
         name='lob'
         OptionValueKey='id'
         OptionLabelKey='name'
         placeholder={`${t(
          'userManagement.activate.businessVertical.lob'
         )}`}
         errors={errors}
        />
       </div>
      </div>
     </form>
    </div>
   )}
  </>
 )
}

export default BusinessVertical
